import React, { useEffect, useRef, useState } from "react";
import PageLayout from "../../components/layouts/PageLayout";
import CustomNavbar from "../../components/common/CustomNavbar";
import { toast } from "sonner";
import ModalLayout from "../../components/layouts/ModalLayout";
import { useNavigate } from "react-router-dom";
import { validateSuperAdminPassword } from "../../features/dashboard/DasboardServices";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import RegiterBUttons from "./RegiterBUttons";
import { getDataUserId } from "../../util/manageAuth";


interface FBAuthResponse {
    code: string;
    accessToken?: string;
}

interface FBLoginResponse {
    authResponse: FBAuthResponse | null;
    status: string;
}

interface FBLoginOptions {
    config_id: string;
    response_type: string;
    override_default_response_type: boolean;
    extras: Record<string, unknown>;
}

export default function Onboarding() {

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const sessionDataRef = useRef<{
        waba_id?: string;
        phone_number_id?: string;
    }>({});

    const [loading, setLoading] = useState(false);
    const [sdkReady, setSdkReady] = useState(false);

    const [isAuthorized, setIsAuthorized] = useState(false);
    const [password, setPassword] = useState("");

    const handlePasswordSubmit = async () => {
        try {
            await dispatch(
                validateSuperAdminPassword({
                    userId: 1, // replace with actual user id
                    password
                })
            ).unwrap();

            toast.success("Access granted");
            setIsAuthorized(true);

        } catch (err: any) {
            toast.error(err || "Invalid password");
        }
    };
    // ==============================
    // Load Facebook SDK
    // ==============================
    useEffect(() => {
        if (window.FB) {
            setSdkReady(true);
            return;
        }

        // ✅ MUST be set BEFORE script is appended
        window.fbAsyncInit = function () {
            window.FB.init({
                appId: "605083631903295",
                autoLogAppEvents: true,
                xfbml: false,
                version: "v21.0",
            });

            console.log("[FB SDK] Initialized");
            setSdkReady(true);
        };

        if (!document.getElementById("fb-sdk")) {
            const script = document.createElement("script");
            script.id = "fb-sdk";
            script.src = "https://connect.facebook.net/en_US/sdk.js";
            script.async = true;
            script.defer = true;
            script.crossOrigin = "anonymous";
            document.body.appendChild(script);
        }
    }, []);

    // ==============================
    // Listen for Embedded Signup Data
    // ==============================
    useEffect(() => {
        const messageListener = (event: MessageEvent) => {
            if (
                event.origin !== "https://www.facebook.com" &&
                event.origin !== "https://web.facebook.com"
            ) {
                return;
            }

            try {
                const data = JSON.parse(event.data);

                if (
                    data.type === "WA_EMBEDDED_SIGNUP" &&
                    data.event === "FINISH"
                ) {
                    const { phone_number_id, waba_id } = data.data;

                    sessionDataRef.current = { phone_number_id, waba_id };

                    console.log("✅ WABA:", waba_id);
                    console.log("✅ Phone Number ID:", phone_number_id);
                }
            } catch (err) {
                console.warn("Message parsing failed", err);
            }
        };

        window.addEventListener("message", messageListener);
        return () => window.removeEventListener("message", messageListener);
    }, []);

    // ==============================
    // Backend Call (async handled here)
    // ==============================
    const handleBackendOnboard = async (code: string) => {
        setLoading(true);

        try {
            const { waba_id, phone_number_id } = sessionDataRef.current;
            const userId = getDataUserId()

            if (!waba_id || !phone_number_id) {
                toast.error("Signup not completed properly. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(
                "https://apiwhat.empedance.com/api/whatsapp/onboard",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        code,
                        wabaId: waba_id,
                        phoneNumberId: phone_number_id,
                        userId: userId,
                    }),
                }
            );

            const text = await response.text();
            console.log("Backend Response:", text);

            if (response.ok && text.toLowerCase().includes("successful")) {
                toast.success("WhatsApp Business Connected Successfully!");
            } else {
                toast.error(text || "Something went wrong. Please try again.");
            }
        } catch (error) {
            console.error("Onboarding failed:", error);
            toast.error("Something went wrong. Check console.");
        } finally {
            setLoading(false);
        }
    };

    // ==============================
    // FB Login Callback (NOT async)
    // ==============================
    const fbLoginCallback = (response: any) => {
        if (!response.authResponse) {
            toast.error("Login cancelled.");
            return;
        }

        const code = response.authResponse.code;
        handleBackendOnboard(code);
    };

    // ==============================
    // Start Embedded Signup
    // ==============================
    const handleOnboard = () => {
        const isLocalhost =
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";

        if (!isLocalhost && window.location.protocol !== "https:") {
            toast.error("Facebook Login requires HTTPS.");
            return;
        }

        if (!sdkReady) {
            toast.error("Facebook SDK not loaded yet. Please wait.");
            return;
        }

        window.FB.login(fbLoginCallback, {
            config_id: "603828229379398",
            response_type: "code",
            override_default_response_type: true,
            extras: { version: "v3" },
        });
    };

    return (
        <>
            {!isAuthorized && (
                <ModalLayout onClose={() => navigate(-1)}>
                    <div className="p-6 w-[350px] flex flex-col gap-4">

                        <h2 className="text-lg font-semibold">Enter Password</h2>

                        <input
                            type="password"
                            placeholder="Enter password"
                            className="border rounded px-3 py-2 text-sm"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handlePasswordSubmit()
                            }
                        />

                        <button
                            className="bg-brand-main text-white py-2 rounded"
                            onClick={handlePasswordSubmit}
                        >
                            Submit
                        </button>

                    </div>
                </ModalLayout>
            )}

            {isAuthorized && (
                <PageLayout
                    customNavbar={
                        <CustomNavbar>
                            <div className="flex gap-3 items-center w-max">
                                <span className="font-bold text-lg uppercase">
                                    Onboarding
                                </span>
                            </div>
                        </CustomNavbar>
                    }
                >
                    <div className="flex flex-col items-center justify-center gap-8 p-10 min-h-[70vh]">
                        <h2 className="text-2xl md:text-3xl font-bold text-center">
                            Connect Your WhatsApp Business Account
                        </h2>

                        <p className="text-gray-600 text-base text-center max-w-lg">
                            You will be securely redirected to Meta's official
                            WhatsApp Business Embedded Signup flow.
                        </p>

                        <button
                            onClick={handleOnboard}
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition shadow-md disabled:opacity-50"
                        >
                            {loading ? "Connecting..." : "Connect WhatsApp Business"}
                        </button>

                        <RegiterBUttons />

                        <p className="text-xs text-gray-500 mt-6 text-center">
                            Powered by Meta Embedded Signup
                        </p>
                    </div>
                </PageLayout>
            )}
        </>
    );
}